import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InvoiceFileType, InvoiceSearchDto } from '@inosio/payments-sdk';
import { format } from 'date-fns';
import NumberFormat from 'react-number-format';
import { makeStyles } from '@mui/styles';
import { DateRange } from '@mui/lab';
import { Card, Stack, Box, Table, TableBody, TableCell, TableContainer } from '@mui/material';
import { TableHead, TableRow, Tooltip, TablePagination } from '@mui/material';
import TableHeader from '../shared/TableHeader';
import ExportFileIcon from '../../icons/exportFileIcon.svg';
import { IFileExportDetails } from '../../components/ExportDataModal';
import { getInvoiceFile, getInvoices, selectBillingState } from './sagas/billingSlice';

const useStyles = makeStyles({
  gridContainer: {
    background: '#FFFFFF',
    borderRadius: '24px',
  },
});

const invoiceSearchOptions = [{ key: 'InvoiceId', value: 'Invoice ID' }];

export default function Invoicing(props: {}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const defaultStartDate = new Date();
  const defaultEndDate = new Date();
  defaultStartDate.setFullYear(defaultStartDate.getFullYear() - 1);

  const { invoices } = useSelector(selectBillingState);

  const [rangeSize, setRangeSize] = React.useState(100);
  const [openCalendarModal, setOpenCalendarModal] = React.useState(false);
  const [dateRangeChipOpen, setDateRangeChipOpen] = React.useState(false);
  const [searchChipOpen, setSearchChipOpen] = React.useState(false);
  const [searchField, setSearchField] = React.useState('');
  const [searchValue, setSearchValue] = React.useState('');
  const searchValueInputRef = React.createRef<HTMLInputElement>();
  const [isSearchValueEmpty, setIsSearchValueEmpty] = React.useState(false);
  const [searchBluePillValue, setSearchPillValue] = React.useState('');
  const columnsSelectRef = React.createRef<HTMLSelectElement>();
  const [columnFilterSearchField, setColumnFilterSearchField] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(0);
  const [startIndex, setStartIndex] = React.useState(0);
  const [calendarWidgetDateRange, setcalendarWidgetDateRange] = React.useState<DateRange<Date>>([
    // Date range to hold what the calendar currently shows
    defaultStartDate,
    defaultEndDate,
  ]);

  const [apiDateRange, setapiDateRange] = React.useState<DateRange<Date>>([
    // Date range to send to the API
    defaultStartDate,
    defaultEndDate,
  ]);

  const handleClickOpen = () => setOpenCalendarModal(true);
  const searchValueChangeHandler = (e: any) => setSearchValue(e.target.value);
  const searchFieldChangeHandler = (e: any) => setSearchField(e.target.value);

  const handleChooseColumnSearchField = (event: any) => {
    event.preventDefault();
    if (columnsSelectRef.current) {
      setColumnFilterSearchField(columnsSelectRef?.current?.value);
    }
  };

  const handleCalendarSubmit = () => {
    setCurrentPage(0);
    setStartIndex(0);
    setOpenCalendarModal(false);
    setapiDateRange(calendarWidgetDateRange);
    setDateRangeChipOpen(true);
  };

  const handleClose = () => setOpenCalendarModal(false);

  const searchInvoices = (event: React.SyntheticEvent, exportDetails?: IFileExportDetails) => {
    event?.preventDefault();
    setSearchChipOpen(true);

    if (searchValueInputRef.current) {
      setSearchPillValue(searchValueInputRef?.current?.value);
    }

    // Search field not set and date range pill not open OR search field not set and date range pill open
    if ((searchField.length === 0 && !dateRangeChipOpen) || (searchField.length === 0 && dateRangeChipOpen)) {
      dispatch({
        type: getInvoices.type,
        payload: {
          EndDate: apiDateRange[1]?.toISOString() || new Date().toISOString(),
          StartDate: apiDateRange[0]?.toISOString() || new Date().toISOString(),
          RangeSize: rangeSize,
          StartIndex: startIndex,
        },
      });

      // search field and value set and date range pill not open
    } else if (searchValue.length !== 0 && searchField.length !== 0 && !dateRangeChipOpen) {
      setCurrentPage(0);
      setStartIndex(0);
      dispatch({
        type: getInvoices.type,
        payload: {
          RangeSize: rangeSize,
          StartIndex: startIndex,
          InvoiceId: searchValue,
        } as InvoiceSearchDto,
      });
    } else {
      setCurrentPage(0);
      dispatch({
        type: getInvoices.type,
        payload: {
          EndDate: apiDateRange[1]?.toISOString() || new Date().toISOString(),
          StartDate: apiDateRange[0]?.toISOString() || new Date().toISOString(),
          RangeSize: rangeSize,
          StartIndex: 0,
          InvoiceId: searchValue,
        } as InvoiceSearchDto,
      });
    }
  };

  const resetTableRows = () => {
    setSearchChipOpen(false);
    setSearchValue('');
    setSearchField('');
    setSearchPillValue('');
    dispatch({
      type: getInvoices.type,
      payload: {
        StartIndex: startIndex,
        RangeSize: rangeSize,
      } as InvoiceSearchDto,
    });
  };

  React.useEffect(() => {
    dispatch({
      type: getInvoices.type,
      payload: {
        StartIndex: startIndex,
        RangeSize: rangeSize,
      } as InvoiceSearchDto,
    });
  }, []);

  React.useEffect(() => {
    if (searchValue.length === 0) {
      setIsSearchValueEmpty(true);
    } else {
      setIsSearchValueEmpty(false);
    }
  }, [searchValue]);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPageNumber: number) => {
    setCurrentPage(newPageNumber);
    setStartIndex(+newPageNumber * +rangeSize);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCurrentPage(0);
    setStartIndex(0);
    setRangeSize(parseInt(event.target.value) / 2);
  };

  const downloadInvoiceFiles = (invoiceId: number) => {
    //download pdf summary
    dispatch({
      type: getInvoiceFile.type,
      invoiceId: invoiceId,
      invoiceFileType: InvoiceFileType.SummaryPdf,
    });

    //download csv detail
    dispatch({
      type: getInvoiceFile.type,
      invoiceId: invoiceId,
      invoiceFileType: InvoiceFileType.DetailCsv,
    });
  };

  return (
    <Box>
      <Card
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc( 100vh - 144px )',
          pb: '64px',
          mt: 3.5,
        }}
        className={classes.gridContainer}
        style={{ position: 'relative' }}
      >
        <>
          <Box sx={{ display: 'flex', p: 1, pb: 2, justifyContent: 'flex-start', '& .MuiTextField-root': { m: 1, width: '25ch' } }}>
            <TableHeader
              dateRangeChipOpen={dateRangeChipOpen}
              calendarWidgetDateRange={apiDateRange}
              setcalendarWidgetDateRange={setcalendarWidgetDateRange}
              defaultEndDate={defaultEndDate}
              defaultStartDate={defaultStartDate}
              setDateRangeChipOpen={setDateRangeChipOpen}
              handleClickOpen={handleClickOpen}
              searchFieldChangeHandler={searchFieldChangeHandler}
              columnsSelectRef={columnsSelectRef}
              handleChooseColumnSearchField={handleChooseColumnSearchField}
              columnFilterSearchField={columnFilterSearchField}
              searchField={searchField}
              optionValues={invoiceSearchOptions}
              handleSearch={searchInvoices}
              searchBluePillValue={searchBluePillValue}
              searchValueInputRef={searchValueInputRef}
              searchValueChangeHandler={searchValueChangeHandler}
              searchValue={searchValue}
              isSearchValueEmpty={isSearchValueEmpty}
              handleClose={handleClose}
              openCalendarModal={openCalendarModal}
              handleCalendarSubmit={handleCalendarSubmit}
              setSearchChipOpen={setSearchChipOpen}
              searchChipOpen={searchChipOpen}
              resetSearchFilter={setSearchField}
              resetSearchValue={setSearchValue}
              resetTableRows={resetTableRows}
            />
          </Box>
          <TableContainer style={{ flex: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell id="subTitle">Date</TableCell>
                  <TableCell id="subTitle">Invoice ID</TableCell>
                  <TableCell id="subTitle">Amount</TableCell>
                  <TableCell id="subTitle">Status</TableCell>
                  <TableCell id="subTitle">
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                      Download Invoice
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody style={{ left: '28px' }}>
                {invoices.length ? (
                  invoices.map((invoice) => {
                    return (
                      <React.Fragment key={invoice.Id}>
                        <TableRow>
                          <TableCell id="content">{`${format(new Date(invoice.CreatedDate), 'MM/dd/yyyy')}`}</TableCell>
                          <TableCell id="content">{`${invoice.Id}`}</TableCell>
                          <TableCell id="content">
                            <NumberFormat
                              thousandsGroupStyle="thousand"
                              value={+invoice.Amount / 100}
                              prefix="$"
                              decimalSeparator="."
                              displayType="text"
                              type="text"
                              thousandSeparator={true}
                              allowNegative={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                            />
                          </TableCell>
                          <TableCell id="content">{invoice.InvoiceStatus === 'Paid' ? `Paid on: ${format(new Date(invoice.PaidOnDate!), 'MM/dd/yyyy')}` : invoice.InvoiceStatus}</TableCell>
                          <TableCell id="content">
                            <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                              <Tooltip title="export invoice" placement="right">
                                <img
                                  src={ExportFileIcon}
                                  alt="export-file"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    downloadInvoiceFiles(invoice.Id);
                                  }}
                                />
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <></>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {!!invoices?.length && (
            <TablePagination
              nextIconButtonProps={{
                disabled: invoices?.length < rangeSize * 2,
              }}
              component="div"
              rowsPerPageOptions={[10, 20, 40]}
              count={invoices?.length < rangeSize * 2 ? invoices?.length : -1}
              page={currentPage}
              onPageChange={handleChangePage}
              rowsPerPage={invoices?.length ? rangeSize : 0}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </>
      </Card>
    </Box>
  );
}
